import { h } from '@/lib/dom';
import { Card } from '@/components/Card';
import { Button } from '@/components/Button';
import { FormField } from '@/components/FormField';
import { Skeleton } from '@/components/Skeleton';
import { MarkerBadge } from '@/components/MarkerBadge';
import { toast } from '@/components/Toast';
import { data } from '@/lib/data';
import { refWithFallback } from '@/lib/format';
import { currentSession } from '@/lib/session';
import type { RouteContext } from '@/router';
import { navigate } from '@/router';
import { pageLayout } from './layout';
import type { ConfirmRequest, LabDraft, MarkerCatalogEntry, ParsedResultDraft } from '@/api/types';

interface EditableRow extends ParsedResultDraft {
  _pendingMarkerId: number | null;
  _editing: boolean;
}

export async function renderLabReview(ctx: RouteContext): Promise<Node> {
  const user = currentSession();
  if (!user) {
    navigate('/login', { replace: true });
    return h('div');
  }
  if (user.role !== 'owner') {
    navigate('/labs', { replace: true });
    return h('div');
  }
  const draftId = Number(ctx.params['draftId']);
  if (!Number.isFinite(draftId)) {
    navigate('/labs/upload', { replace: true });
    return h('div');
  }

  const contentEl = h('div', null, Skeleton({ variant: 'rect', height: '400px' }));

  const root = pageLayout({
    user,
    active: 'labs',
    children: [
      h('div', { class: 'breadcrumb' }, [
        h('a', { href: '/labs/upload' }, '← Назад к загрузке'),
      ]),
      h('h1', { class: 'page-title mb-2' }, 'Проверить и подтвердить'),
      h('p', { class: 'text-secondary mb-4', style: 'max-width: 72ch;' },
        'Claude распознал значения из PDF. Просмотри таблицу ниже, исправь ошибки, '
        + 'при необходимости свяжи «не распознанные» маркёры со справочником, '
        + 'затем нажми «Подтвердить и сохранить». '
        + 'Без подтверждения данные НЕ попадут в дашборд и тренды.'),
      contentEl,
    ],
  });

  void (async () => {
    try {
      const [draft, catalog] = await Promise.all([
        data.getDraft(draftId),
        data.getCatalog(),
      ]);
      if (!draft) {
        throw new Error('Черновик не найден');
      }
      contentEl.innerHTML = '';
      const initialRows: EditableRow[] = draft.results.map((r) => ({
        ...r,
        _pendingMarkerId: r.marker_id,
        _editing: false,
      }));
      if (!initialRows.length) {
        contentEl.appendChild(h('div', { class: 'empty-state' }, [
          h('p', null, 'В этом PDF не распознано ни одного маркёра.'),
          h('p', { class: 'text-tertiary mt-2' },
            'Возможно, файл повреждён или не содержит табличных лабораторных данных.'),
          h('a', { class: 'btn btn-secondary mt-2', href: '/labs/upload' }, 'Загрузить другой файл'),
        ]));
        return;
      }
      renderReview(contentEl, draftId, draft, initialRows, catalog);
      document.dispatchEvent(new Event('route:settled'));
    } catch (err) {
      contentEl.innerHTML = '';
      contentEl.appendChild(h('div', { class: 'empty-state', role: 'alert' }, [
        h('p', null, `Не удалось загрузить черновик: ${err instanceof Error ? err.message : 'неизвестная ошибка'}`),
        h('button', {
          class: 'btn btn-secondary mt-2',
          type: 'button',
          on: { click: () => navigate('/labs/upload', { replace: true }) },
        }, 'Вернуться к загрузке'),
      ]));
      console.error(err);
    }
  })();

  return root;
}

function renderReview(
  root: HTMLElement,
  draftId: number,
  draft: LabDraft,
  rows: EditableRow[],
  catalog: MarkerCatalogEntry[],
): void {
  let drawnAt = draft.drawn_at;
  let labName = draft.lab_name ?? '';
  let saving = false;

  const dateInput = h('input', {
    class: 'form-input',
    type: 'date',
    value: drawnAt,
    on: { change: (e: Event) => { drawnAt = (e.target as HTMLInputElement).value; } },
  }) as HTMLInputElement;
  const labInput = h('input', {
    class: 'form-input',
    type: 'text',
    value: labName,
    on: { input: (e: Event) => { labName = (e.target as HTMLInputElement).value; } },
  }) as HTMLInputElement;

  const tableEl = h('div', { id: 'review-table' });
  renderRows();

  function renderRows(): void {
    tableEl.innerHTML = '';
    tableEl.appendChild(
      h('div', { class: 'mb-4 text-tertiary', style: 'font-size: 13px;' },
        `${draft.pdf_filename} · ${rows.length} маркёров · дата забора ${drawnAt}`),
    );
    const table = h('table', {
      class: 'data-table',
      role: 'table',
      aria: { label: 'Распознанные значения' },
    }, [
      h('thead', null,
        h('tr', null, [
          h('th', null, 'Распозн.'),
          h('th', null, 'Маркёр (норм.)'),
          h('th', null, 'Значение'),
          h('th', null, 'Ед.'),
          h('th', null, 'Референс'),
          h('th', null, 'Флаг'),
          h('th', null, ''),
        ])),
      h('tbody', null,
        rows.map((row) => {
          const marker = catalog.find((m) => m.id === row._pendingMarkerId) ?? null;
          return h('tr', null, [
            h('td', null, row.raw_name),
            h('td', null, row._editing
              ? renderMarkerSelect(catalog, row._pendingMarkerId, (id) => {
                  row._pendingMarkerId = id;
                  renderRows();
                })
              : marker
                ? h('span', null, `${marker.display_ru} (${marker.display_en})`)
                : h('span', { style: 'color: var(--status-warn-fg);' }, '⚠ Не распознан')),
            h('td', { style: 'text-align:right;' }, row._editing
              ? renderValueInput(row, () => renderRows())
              : h('span', { class: 'tabular' }, row.value != null ? String(row.value) : row.qualitative_value ?? row.raw_value)),
            h('td', null, row.unit ?? row.raw_unit ?? '—'),
            (() => {
              const ref = refWithFallback(row.ref_low, row.ref_high, marker?.ref_low_default, marker?.ref_high_default);
              return h('td', ref.fromCatalog
                ? { class: 'text-tertiary', title: 'Из справочника — в PDF нет числового диапазона' }
                : null, ref.text);
            })(),
            h('td', null, MarkerBadge({ flag: row.flag })),
            h('td', null,
              h('button', {
                class: 'btn btn-ghost btn-sm',
                type: 'button',
                aria: { label: row._editing ? 'Сохранить строку' : 'Редактировать' },
                on: {
                  click: () => {
                    row._editing = !row._editing;
                    renderRows();
                  },
                },
              }, row._editing ? 'Готово' : '✎')),
          ]);
        })),
    ]);
    tableEl.appendChild(h('div', { class: 'data-table-scroll' }, table));
    const unresolved = rows.filter((r) => r._pendingMarkerId == null).length;
    if (unresolved > 0) {
      tableEl.appendChild(h('p', { class: 'text-secondary mt-2', style: 'font-size: 13px;' },
        `⚠ ${unresolved} маркёр(ов) не связано со справочником — их можно связать через ✎, `
        + 'либо оставить как есть: они будут пропущены при сохранении.'));
    }
  }

  const footer = h('div', {
    class: 'mt-6 flex gap-3 labs-review-footer',
    style: 'justify-content: flex-end;',
  });

  function setFooter(): void {
    footer.innerHTML = '';
    const resolvedCount = rows.filter((r) => r._pendingMarkerId != null).length;
    const totalCount = rows.length;
    const unresolved = totalCount - resolvedCount;
    const btnLabel = saving
      ? 'Сохраняем…'
      : unresolved > 0
        ? `Подтвердить ${resolvedCount} из ${totalCount}`
        : `Подтвердить и сохранить (${resolvedCount})`;
    footer.append(
      Button({ label: 'Отмена', variant: 'secondary', onClick: () => navigate('/labs/upload') }),
      Button({
        label: btnLabel,
        variant: 'primary',
        disabled: saving || resolvedCount === 0,
        onClick: async () => {
          saving = true;
          setFooter();
          try {
            const payload: ConfirmRequest = {
              drawn_at: drawnAt,
              lab_name: labName || null,
              results: rows
                .filter((r) => r._pendingMarkerId != null)
                .map((r) => {
                  const marker = catalog.find((m) => m.id === r._pendingMarkerId)!;
                  return {
                    marker_id: marker.id,
                    raw_name: r.raw_name,
                    raw_value: r.raw_value,
                    raw_unit: r.raw_unit,
                    value: r.value,
                    unit: r.unit ?? marker.canonical_unit,
                    qualitative_value: r.qualitative_value,
                    ref_low: r.ref_low,
                    ref_high: r.ref_high,
                    flag: (r.flag as 'H' | 'L' | 'N' | 'HH' | 'LL' | null) ?? null,
                    is_edited: false,
                  };
                }),
            };
            await data.confirmLab(draftId, payload);
            toast('Анализ сохранён', { type: 'success' });
            navigate('/labs');
          } catch (err) {
            saving = false;
            setFooter();
            toast(err instanceof Error ? err.message : 'Не удалось сохранить', { type: 'error' });
          }
        },
      }),
    );
  }
  setFooter();

  root.append(
    Card({
      title: '',
      children: h('div', { class: 'flex gap-4', style: 'flex-wrap: wrap;' }, [
        h('div', { style: 'flex: 1; min-width: 220px;' },
          FormField({ label: 'Дата забора', required: true, children: dateInput })),
        h('div', { style: 'flex: 1; min-width: 220px;' },
          FormField({ label: 'Лаборатория', children: labInput })),
      ]),
    }),
    h('div', { class: 'mt-6' }, tableEl),
    footer,
  );
}

function renderMarkerSelect(
  catalog: MarkerCatalogEntry[],
  currentId: number | null,
  onChange: (id: number | null) => void,
): HTMLElement {
  const select = h('select', {
    class: 'form-select',
    on: {
      change: (ev: Event) => {
        const v = (ev.target as HTMLSelectElement).value;
        onChange(v ? Number(v) : null);
      },
    },
  }, [
    h('option', { value: '' }, '— не связан —'),
    ...catalog.map((m) => {
      const opt = h('option', { value: String(m.id) }, `${m.display_ru} (${m.display_en})`);
      if (m.id === currentId) (opt as HTMLOptionElement).selected = true;
      return opt;
    }),
  ]) as HTMLSelectElement;
  return select;
}

function renderValueInput(row: EditableRow, onChange: () => void): HTMLElement {
  return h('input', {
    class: 'form-input',
    type: 'text',
    value: row.value != null ? String(row.value) : row.raw_value,
    style: 'width: 100px; text-align: right;',
    on: {
      input: (ev: Event) => {
        const v = (ev.target as HTMLInputElement).value;
        const n = Number(v.replace(',', '.'));
        if (Number.isFinite(n)) {
          row.value = n;
          row.raw_value = v;
        } else {
          row.qualitative_value = v;
        }
        onChange();
      },
    },
  }) as HTMLInputElement;
}
